import { CommonModule } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { PipesModule } from '@freelancer/pipes';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { FileDisplayType } from '@freelancer/ui/file-display';
import { blurHashToDataUrl } from '@freelancer/ui/helpers';
import { IconSize } from '@freelancer/ui/icon';
import { MediaViewerComponent } from '@freelancer/ui/media-viewer';
import type { AspectRatioValue } from '@freelancer/ui/picture';
import {
  PictureLayout,
  PictureLoadMethod,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { TextSize } from '@freelancer/ui/text';
import { VideoComponent, VideoObjectFit } from '@freelancer/ui/video';
import type { GalleryMosaicInteractiveContentComponent } from './gallery-mosaic-interactive-content.component';
import { RoundCornerType, SingleImageFit } from './gallery-mosaic.types';
import type { GalleryItem } from '../gallery-types/gallery.types';
import { GalleryItemType } from '../gallery-types/gallery.types';

@Component({
  standalone: true,
  selector: 'fl-gallery-mosaic',
  imports: [
    CommonModule,
    PipesModule,
    TrackingModule,
    UiModule,
    MediaViewerComponent,
    VideoComponent,
  ],
  template: `
    <div
      class="GalleryGrid"
      [attr.center-content]="centerContent"
      [attr.item-spacing]="itemSpacing"
    >
      @for (
        galleryItem of galleryItems | slice: 0 : displayedGalleryItemsModern;
        track trackByItemSource(i, galleryItem);
        let i = $index;
        let isFirst = $first
      ) {
        <div
          tabindex="0"
          [attr.item-spacing]="itemSpacing"
          [attr.aria-label]="galleryItem.fileName"
          [class.OneItem]="galleryItems.length === 1"
          [class.TwoItems]="galleryItems.length === 2"
          [class.ThreeItems]="galleryItems.length === 3"
          [class.FourItems]="galleryItems.length >= 4 && !forceRectangleMedium"
          [class.FiveItems]="galleryItems.length >= 5 && forceRectangleMedium"
          [class.ReverseGallery]="reverseGallery"
          [ngSwitch]="galleryItem.type"
          (click)="isViewerEnabled && handleGalleryItemClicked(i, $event)"
        >
          <div>
            <ng-container *ngSwitchCase="GalleryItemType.PICTURE">
              @if (!errorItems.includes(galleryItem)) {
                <div
                  class="GridColumn-pictureContainer"
                  [attr.item-spacing]="itemSpacing"
                  [attr.rounded-corners]="roundGalleryCorners"
                  [class.InteractiveContent]="interactiveContent"
                  [class.LinkEntireTile]="interactiveContent && linkEntireTile"
                  [class.GridColumn-pictureContainer-oneItem]="
                    singleImageFit === 'contain' && isSingleImageGallery
                  "
                >
                  <div class="ImageWrapper">
                    <!-- Blurhash placeholder for single image -->
                    @if (isSingleImageGallery && blurhashDataUrls[0]) {
                      <img
                        class="GridColumn-pictureContainer-blurhash"
                        [class.BlurhashHidden]="!loadingItems[0]"
                        [src]="blurhashDataUrls[0]"
                        [alt]="galleryItem.alt || galleryItem.fileName"
                      />
                    }
                    @if (isSingleImageGallery) {
                      <fl-picture
                        class="GridColumn-pictureContainer-picture"
                        [class.ZoomOnHover]="zoomOnHover"
                        [class.Clickable]="isViewerEnabled"
                        [alignCenter]="true"
                        [alt]="galleryItem.alt || galleryItem.fileName"
                        [src]="galleryItem.source"
                        [width]="galleryItem.width"
                        [height]="galleryItem.height"
                        [boundedWidth]="singleImageFit === 'contain'"
                        [boundedHeight]="singleImageFit === 'contain'"
                        [aspectRatioValue]="
                          singleImageFit === 'cover' ||
                          loadMethod === PictureLoadMethod.LAZY
                            ? firstGalleryItemAspectRatio
                            : undefined
                        "
                        [objectFit]="
                          singleImageFit === 'contain'
                            ? PictureObjectFit.CONTAIN
                            : PictureObjectFit.COVER
                        "
                        [layout]="
                          usingFastlyIO ? PictureLayout.RESPONSIVE : undefined
                        "
                        [loadMethod]="loadMethod"
                        [sizes]="usingFastlyIO ? imageSizes : undefined"
                        (imageLoaded)="stopLoading(0)"
                        (onError)="handleImageLoadError(galleryItem)"
                      />
                    } @else {
                      <!-- Blurhash placeholder for multiple images -->
                      @if (blurhashDataUrls[i]) {
                        <img
                          class="GridColumn-pictureContainer-blurhash"
                          [attr.rounded-corners]="roundGalleryCorners"
                          [class.BlurhashHidden]="!loadingItems[i]"
                          [src]="blurhashDataUrls[i]"
                          [alt]="galleryItem.alt || galleryItem.fileName"
                        />
                      }
                      <fl-picture
                        class="GridColumn-pictureContainer-picture"
                        [attr.rounded-corners]="roundGalleryCorners"
                        [class.ZoomOnHover]="zoomOnHover"
                        [class.Clickable]="isViewerEnabled"
                        [alignCenter]="true"
                        [alt]="galleryItem.alt || galleryItem.fileName"
                        [aspectRatioValue]="
                          isFirst
                            ? firstGalleryItemAspectRatio
                            : galleryItemAspectRatio
                        "
                        [borderRadiusTop]="
                          roundGalleryCorners === RoundCornerType.ITEM
                        "
                        [src]="galleryItem.source"
                        [objectFit]="PictureObjectFit.COVER"
                        [layout]="
                          usingFastlyIO ? PictureLayout.RESPONSIVE : undefined
                        "
                        [loadMethod]="loadMethod"
                        [sizes]="usingFastlyIO ? imageSizes : undefined"
                        (imageLoaded)="stopLoading(i)"
                        (onError)="handleImageLoadError(galleryItem)"
                      ></fl-picture>
                    }
                    @if (
                      galleryItem.hoverOverlayColor ||
                      galleryItem.hoverOverlayText
                    ) {
                      <!-- eslint-disable-next-line @angular-eslint/template/no-inline-styles -->
                      <div
                        class="HoverOverlay"
                        [style.--hover-overlay-color]="
                          galleryItem.hoverOverlayColor
                        "
                      ></div>
                      @if (galleryItem.hoverOverlayText) {
                        <span
                          class="HoverOverlay-text font-barlow-condensed"
                          [class.HoverOverlay-text--large]="isFirst"
                        >
                          {{ galleryItem.hoverOverlayText }}
                        </span>
                      }
                    }
                    @if (interactiveContent && linkEntireTile) {
                      <fl-link
                        class="TileLink"
                        [flTrackingLabel]="
                          galleryItem.trackingLabel ?? galleryItem.fileName
                        "
                        [label]="
                          galleryItem.linkLabel ||
                          galleryItem.alt ||
                          galleryItem.fileName
                        "
                        [link]="galleryItem.link ?? '/post-project'"
                        [queryParams]="galleryItem.queryParams"
                      />
                    }
                  </div>
                  @if (
                    !!galleryItem.link &&
                    !(interactiveContent && linkEntireTile)
                  ) {
                    <fl-link
                      class="Link"
                      [label]="
                        galleryItem.linkLabel ||
                        galleryItem.alt ||
                        galleryItem.fileName
                      "
                      [link]="galleryItem.link"
                    />
                  }
                  @if (interactiveContent) {
                    <div class="ContentWrapper">
                      <div class="Content">
                        <ng-container
                          [ngTemplateOutlet]="galleryInteractiveContent"
                          [ngTemplateOutletContext]="{ $implicit: i }"
                        />
                      </div>
                    </div>
                  }
                  @if (loadingItems[i]) {
                    <fl-spinner
                      class="GridColumn-pictureContainer-loading"
                      [attr.rounded-corners]="roundGalleryCorners"
                      [class.GridColumn-pictureContainer-loading-background]="
                        interactiveContent
                      "
                      [size]="SpinnerSize.SMALL"
                    />
                  }
                </div>
              }
              @if (errorItems.includes(galleryItem)) {
                <div class="GridColumn-pictureLoadError">
                  <div class="GridColumn-pictureLoadError-inner">
                    <fl-icon
                      class="GalleryFileDisplay mb-xxxsmall"
                      [name]="'ui-broken-image'"
                      [size]="IconSize.SMALL"
                      [sizeTablet]="IconSize.MID"
                      [sizeDesktop]="IconSize.XLARGE"
                      [useIconFont]="true"
                      [fillIconFont]="true"
                    />
                    <span
                      class="text-foreground text-body-16 text-center"
                      i18n="Image Failed To Load"
                    >
                      Failed to load
                    </span>
                  </div>
                </div>
              }
            </ng-container>
            <ng-container *ngSwitchCase="GalleryItemType.VIDEO">
              @if (inlineVideos) {
                @if (!errorItems.includes(galleryItem)) {
                  <div
                    class="GridColumn-pictureContainer"
                    [attr.item-spacing]="itemSpacing"
                    [attr.rounded-corners]="roundGalleryCorners"
                    [class.InteractiveContent]="interactiveContent"
                    [class.LinkEntireTile]="
                      interactiveContent && linkEntireTile
                    "
                  >
                    <div class="ImageWrapper">
                      <div
                        class="GridColumn-pictureContainer-video GridColumn-pictureContainer-picture"
                        [attr.rounded-corners]="roundGalleryCorners"
                        [attr.data-aspect-ratio-value]="
                          isFirst
                            ? firstGalleryItemAspectRatio
                            : galleryItemAspectRatio
                        "
                      >
                        <fl-video
                          class="GridColumn-pictureContainer-videoPlayer"
                          [autoPlay]="true"
                          [disableControls]="true"
                          [isExternal]="galleryItem.isExternal ?? true"
                          [loop]="true"
                          [objectFit]="VideoObjectFit.COVER"
                          [src]="galleryItem.source"
                          [thumbnail]="galleryItem.thumbnailSource"
                          (canPlayThrough)="stopLoading(i)"
                          (error)="handleImageLoadError(galleryItem)"
                        ></fl-video>
                      </div>
                      @if (
                        galleryItem.hoverOverlayColor ||
                        galleryItem.hoverOverlayText
                      ) {
                        <!-- eslint-disable-next-line @angular-eslint/template/no-inline-styles -->
                        <div
                          class="HoverOverlay"
                          [style.--hover-overlay-color]="
                            galleryItem.hoverOverlayColor
                          "
                        ></div>
                        @if (galleryItem.hoverOverlayText) {
                          <span
                            class="HoverOverlay-text font-barlow-condensed"
                            [class.HoverOverlay-text--large]="isFirst"
                          >
                            {{ galleryItem.hoverOverlayText }}
                          </span>
                        }
                      }
                      @if (interactiveContent && linkEntireTile) {
                        <fl-link
                          class="TileLink"
                          [flTrackingLabel]="
                            galleryItem.trackingLabel ?? galleryItem.fileName
                          "
                          [label]="
                            galleryItem.linkLabel ||
                            galleryItem.alt ||
                            galleryItem.fileName
                          "
                          [link]="galleryItem.link ?? '/post-project'"
                          [queryParams]="galleryItem.queryParams"
                        />
                      }
                    </div>
                    @if (
                      !!galleryItem.link &&
                      !(interactiveContent && linkEntireTile)
                    ) {
                      <fl-link
                        class="Link"
                        [label]="
                          galleryItem.linkLabel ||
                          galleryItem.alt ||
                          galleryItem.fileName
                        "
                        [link]="galleryItem.link"
                      />
                    }
                    @if (interactiveContent) {
                      <div class="ContentWrapper">
                        <div class="Content">
                          <ng-container
                            [ngTemplateOutlet]="galleryInteractiveContent"
                            [ngTemplateOutletContext]="{ $implicit: i }"
                          />
                        </div>
                      </div>
                    }
                    @if (loadingItems[i]) {
                      <fl-spinner
                        class="GridColumn-pictureContainer-loading"
                        [attr.rounded-corners]="roundGalleryCorners"
                        [class.GridColumn-pictureContainer-loading-background]="
                          interactiveContent
                        "
                        [size]="SpinnerSize.SMALL"
                      />
                    }
                  </div>
                }
                @if (errorItems.includes(galleryItem)) {
                  <div class="GridColumn-pictureLoadError">
                    <div class="GridColumn-pictureLoadError-inner">
                      <fl-icon
                        class="GalleryFileDisplay mb-xxxsmall"
                        [name]="'ui-broken-image'"
                        [size]="IconSize.SMALL"
                        [sizeTablet]="IconSize.MID"
                        [sizeDesktop]="IconSize.XLARGE"
                        [useIconFont]="true"
                        [fillIconFont]="true"
                      />
                      <span
                        class="text-foreground text-body-16 text-center"
                        i18n="Image Failed To Load"
                      >
                        Failed to load
                      </span>
                    </div>
                  </div>
                }
              } @else {
                <div
                  class="GridColumn-filePreview"
                  [class.Clickable]="isViewerEnabled"
                >
                  <div class="GridColumn-filePreview-inner">
                    <fl-file-display
                      class="GalleryFileDisplay mb-xxxsmall"
                      [alt]="galleryItem.fileName"
                      [filename]="galleryItem.fileName"
                      [iconSize]="IconSize.SMALL"
                      [iconSizeTablet]="IconSize.MID"
                      [iconSizeDesktop]="IconSize.XLARGE"
                      [src]="galleryItem.source"
                      [type]="FileDisplayType.ICON"
                    />
                    <span class="text-foreground text-body-16 text-center">
                      {{ galleryItem.fileName | truncateFilename: 20 }}
                    </span>
                  </div>
                </div>
              }
            </ng-container>
            <!-- When the type is GalleryItemType.FILE or GalleryItemType.PDF -->
            <div
              *ngSwitchDefault
              class="GridColumn-filePreview"
              [class.Clickable]="isViewerEnabled"
            >
              <div class="GridColumn-filePreview-inner">
                <fl-file-display
                  class="GalleryFileDisplay mb-xxxsmall"
                  [alt]="galleryItem.fileName"
                  [filename]="galleryItem.fileName"
                  [iconSize]="IconSize.SMALL"
                  [iconSizeTablet]="IconSize.MID"
                  [iconSizeDesktop]="IconSize.XLARGE"
                  [src]="galleryItem.source"
                  [type]="FileDisplayType.ICON"
                />
                <span class="text-foreground text-body-16 text-center">
                  {{ galleryItem.fileName | truncateFilename: 20 }}
                </span>
              </div>
            </div>
            @if (
              i === displayedGalleryItemsModern - 1 &&
              galleryItems.length > displayedGalleryItemsModern &&
              !loadingItems[i]
            ) {
              <div
                class="GridColumn-cover"
                [attr.item-spacing]="itemSpacing"
                [attr.rounded-corners]="roundGalleryCorners"
                [class.Clickable]="isViewerEnabled"
              >
                <h2
                  class="text-light text-body-24 ItemCounter tablet:text-title-32 desktop:text-title-40 font-light"
                >
                  + {{ galleryItems.length - displayedGalleryItemsModern }}
                </h2>
              </div>
            }
          </div>
        </div>
      }
    </div>

    @if (isViewerEnabled && galleryItems?.length > 0 && clickedViewer) {
      <fl-media-viewer
        #galleryViewer
        [mode]="'modal'"
        [galleryItems]="galleryItems"
        [downloadable]="isGalleryItemsDownloadable"
        (loaded)="handleLoadedMediaViewer()"
      />
    }
  `,
  styleUrls: ['./gallery-mosaic.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryMosaicComponent implements OnChanges {
  private changeDetectorRef = inject(ChangeDetectorRef);

  FileDisplayType = FileDisplayType;
  GalleryItemType = GalleryItemType;
  IconSize = IconSize;
  PictureLayout = PictureLayout;
  PictureLoadMethod = PictureLoadMethod;
  PictureObjectFit = PictureObjectFit;
  RoundCornerType = RoundCornerType;
  SpinnerSize = SpinnerSize;
  VideoObjectFit = VideoObjectFit;

  displayedGalleryItemsModern = 4;

  @Input() centerContent = false;
  @Input() galleryItems: readonly GalleryItem[];

  @Input() textSize?: TextSize;
  @Input() textSizeTablet?: TextSize;
  @Input() textSizeDesktop?: TextSize;
  @Input() zoomOnHover = true;

  @Input() usingFastlyIO = false;
  @Input() maxWidth?: number;
  @Input() imageSizes?: string;

  @Input() isViewerEnabled = false;
  @Input() isGalleryItemsDownloadable = true;

  /** This input only works when `forceRectangleMedium` is applied */
  @Input() reverseGallery = false;

  /** Forces the cover items to be rectangular */
  @Input() forceRectangleMedium = false;

  @Input() interactiveContent = false;

  /** How a single image should fit in the container. */
  @Input() singleImageFit: SingleImageFit = 'cover';

  @Input() pictureObjectFit?: PictureObjectFit;

  /** Rounds the gallery's container or item corners */
  @HostBinding('attr.rounded-corners')
  @Input()
  roundGalleryCorners?: RoundCornerType;

  /** Total spacing between each gallery item */
  @HostBinding('attr.item-spacing')
  @Input()
  itemSpacing: 'xxxsmall' | 'xxsmall' | 'xsmall' = 'xxxsmall';

  /** When true, VIDEO items render inline playback instead of a file icon preview */
  @Input() inlineVideos = false;

  /**
   * When true with `interactiveContent`, the image/video area links to
   * `galleryItem.link` (default `/post-project`) including `queryParams`.
   */
  @Input() linkEntireTile = false;

  /** This variable defines how an image is to be loaded */
  @Input()
  loadMethod?: PictureLoadMethod;

  @Output() openItem = new EventEmitter<number>();

  @ViewChild('galleryViewer', { read: MediaViewerComponent, static: false })
  galleryViewer: MediaViewerComponent;

  @ContentChild(TemplateRef)
  galleryInteractiveContent: TemplateRef<GalleryMosaicInteractiveContentComponent>;

  maxColumnWidth?: number;
  errorItems: readonly GalleryItem[] = [];
  loadingItems: readonly boolean[] = [];
  blurhashDataUrls: readonly (string | null)[] = [];

  firstGalleryItemAspectRatio: AspectRatioValue;
  galleryItemAspectRatio: AspectRatioValue;

  isSingleImageGallery = false;
  clickedViewer = false;
  clickedIndex: number | undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      ('maxWidth' in changes || 'galleryItems' in changes) &&
      this.galleryItems
    ) {
      this.maxColumnWidth = this.maxWidth
        ? // Largest image in this layout takes the whole width of the container
          // NOTE: Keep this in sync as the layout changes
          this.galleryItems.length === 1 ||
          this.galleryItems.length === 3 ||
          this.galleryItems.length === 4
          ? this.maxWidth
          : this.maxWidth / 2
        : undefined;
    }

    if ('forceRectangleMedium' in changes) {
      this.displayedGalleryItemsModern = this.forceRectangleMedium ? 5 : 4;
    }

    if ('galleryItems' in changes && this.galleryItems) {
      // Maintain the loading items
      if (this.displayedGalleryItemsModern > this.loadingItems.length) {
        this.loadingItems = [
          ...this.loadingItems,
          ...Array.from(
            new Array(
              this.displayedGalleryItemsModern - this.loadingItems.length,
            ),
            (item, index) =>
              this.galleryItems && this.galleryItems.length > index
                ? this.galleryItems[index].type === GalleryItemType.PICTURE ||
                  (this.galleryItems[index].type === GalleryItemType.VIDEO &&
                    this.inlineVideos)
                : true,
          ),
        ];
      } else {
        this.loadingItems = this.loadingItems.slice(
          0,
          this.displayedGalleryItemsModern,
        );
      }

      // Pre-compute blurhash data URLs
      this.updateBlurhashDataUrls();
      switch (this.galleryItems.length) {
        case 1: {
          this.firstGalleryItemAspectRatio = 'rectangle-medium';
          this.galleryItemAspectRatio = 'rectangle-medium';

          const [firstGalleryItem] = this.galleryItems;
          this.isSingleImageGallery =
            firstGalleryItem.type === GalleryItemType.PICTURE;

          break;
        }

        case 2: {
          if (this.forceRectangleMedium) {
            this.firstGalleryItemAspectRatio = 'rectangle-medium';
            this.galleryItemAspectRatio = 'rectangle-medium';
          } else {
            this.firstGalleryItemAspectRatio = 'square';
            this.galleryItemAspectRatio = 'square';
          }
          break;
        }

        case 3: {
          this.firstGalleryItemAspectRatio = 'rectangle-large';
          this.galleryItemAspectRatio = 'rectangle-medium';
          break;
        }

        case 4: {
          this.firstGalleryItemAspectRatio = 'rectangle-large';
          this.galleryItemAspectRatio = this.forceRectangleMedium
            ? 'rectangle-medium'
            : 'square';
          break;
        }

        default:
          this.firstGalleryItemAspectRatio = 'rectangle-medium';

          if (this.forceRectangleMedium) {
            this.galleryItemAspectRatio = 'rectangle-medium';
          } else {
            this.galleryItemAspectRatio = 'square';
          }
      }
    }
  }

  handleGalleryItemClicked(index: number, event: MouseEvent): void {
    this.openItem.emit(index);

    // Lazy load the media viewer.
    if (!this.clickedViewer) {
      this.clickedViewer = true;
      this.clickedIndex = index;
      return;
    }

    // The media viewer is already loaded, so just open the modal.
    this.openMediaViewer(index);
  }

  private openMediaViewer(index: number): void {
    if (this.isViewerEnabled) {
      if (this.galleryViewer.isModalOpen()) {
        this.galleryViewer.goToItem(index);
      } else {
        this.galleryViewer.openModal(index);
      }
    }
  }

  /**
   * We are lazy loading the fl-media-viewer after the user clicks the image.
   * This function is called after the media viewer is loaded. It then attempts to
   * open the media viewer.
   */
  handleLoadedMediaViewer(): void {
    setTimeout(() => {
      if (this.clickedIndex !== undefined) {
        this.openMediaViewer(this.clickedIndex);
        this.clickedIndex = undefined;
      }
    });
  }

  handleImageLoadError(galleryItem: GalleryItem): void {
    this.errorItems = [...this.errorItems, galleryItem];
    const index = this.galleryItems.indexOf(galleryItem);
    this.stopLoading(index);
  }

  stopLoading(index: number): void {
    this.loadingItems = Object.assign([], this.loadingItems, {
      [index]: false,
    });
    // Trigger change detection to hide blurhash
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Pre-computes blurhash data URLs for all gallery items to avoid function calls in templates
   */
  private updateBlurhashDataUrls(): void {
    if (!this.galleryItems) {
      this.blurhashDataUrls = [];
      return;
    }

    this.blurhashDataUrls = this.galleryItems.map(galleryItem =>
      galleryItem.blurHash
        ? (blurHashToDataUrl(galleryItem.blurHash) ?? null)
        : null,
    );
  }

  trackByItemSource(_: number, item: GalleryItem): string {
    return item.source;
  }
}
